/**
 * The ornament: a compass, a branch, a knot and a constellation, each drawn
 * once here and used everywhere. All original, drawn for this site; nothing
 * is taken from any book's covers or illustrations, and nothing may be added
 * from them (see ip-safety).
 *
 * Every one is decoration, so every one is `aria-hidden`. Colours come from
 * the palette's custom properties through the `orn--*` classes in styles.css,
 * never from `currentColor` or a hex value written here, so the tokens stay
 * the only place a colour is decided.
 */

/** The header mark, on ink. */
export function CompassMark() {
  return (
    <svg className="compass-mark" viewBox="0 0 32 32" width="26" height="26" aria-hidden="true">
      <circle className="orn--brass-lit" cx="16" cy="16" r="13" fill="none" strokeWidth="1.1" />
      <path
        className="orn--brass-lit"
        d="M16 3 L18 14 L29 16 L18 18 L16 29 L14 18 L3 16 L14 14 Z"
        fill="none"
        strokeWidth="1"
      />
    </svg>
  )
}

/** The large compass. On paper it has an inner ring and an oxblood star; on ink, brass only and no inner ring. */
export function Compass({ size, on }: { size: number; on: 'paper' | 'ink' }) {
  const ring = on === 'paper' ? 'orn--brass' : 'orn--brass-lit'
  const star = on === 'paper' ? 'orn--oxblood' : 'orn--brass-lit'
  return (
    <svg className="compass" viewBox="0 0 110 110" width={size} height={size} aria-hidden="true">
      {/* On ink the compass sits over the constellation; its own ground keeps the lines behind it. */}
      {on === 'ink' ? <circle className="orn--fill-ink" cx="55" cy="55" r="44" /> : null}
      <circle className={ring} cx="55" cy="55" r="44" fill="none" strokeWidth="1.2" />
      {on === 'paper' ? <circle className="orn--rule" cx="55" cy="55" r="36" fill="none" strokeWidth="0.8" /> : null}
      <path
        className={star}
        d="M55 13 L61 49 L97 55 L61 61 L55 97 L49 61 L13 55 L49 49 Z"
        fill="none"
        strokeWidth="1.1"
      />
      <path className={ring} d="M80 30 L63 47 M30 80 L47 63 M30 30 L47 47 M80 80 L63 63" strokeWidth="0.7" />
      <circle className={on === 'paper' ? 'orn--fill-oxblood' : 'orn--fill-brass-lit'} cx="55" cy="55" r="3" />
    </svg>
  )
}

/** In a series card's top-right corner, partly off the edge, as on the board. */
export function CompassCorner() {
  return (
    <svg className="compass-corner" viewBox="0 0 60 60" aria-hidden="true">
      <circle className="orn--brass" cx="30" cy="30" r="24" fill="none" strokeWidth="1" />
      <path className="orn--brass" d="M30 6 L33 27 L54 30 L33 33 L30 54 L27 33 L6 30 L27 27 Z" fill="none" strokeWidth="0.9" />
    </svg>
  )
}

/** Above a section heading. */
export function Branch() {
  return (
    <svg className="branch" viewBox="0 0 520 44" aria-hidden="true">
      <path
        className="orn--brass"
        d="M20 32 C 90 32, 130 12, 200 12 C 240 12, 258 22, 260 22 C 262 22, 280 12, 320 12 C 390 12, 430 32, 500 32"
        fill="none"
        strokeWidth="1.1"
      />
      <path
        className="orn--brass"
        d="M150 18 C 140 8, 128 8, 120 14 C 130 22, 144 22, 150 18 Z"
        fill="none"
        strokeWidth="0.9"
      />
      <path
        className="orn--brass"
        d="M370 18 C 380 8, 392 8, 400 14 C 390 22, 376 22, 370 18 Z"
        fill="none"
        strokeWidth="0.9"
      />
      <circle className="orn--oxblood" cx="260" cy="22" r="3.4" fill="none" strokeWidth="1.2" />
    </svg>
  )
}

/** Between sections, and above the footer. Stretches to the width it is given; the knot stays in the middle. */
export function Knot() {
  return (
    <svg className="knot" viewBox="0 0 1168 30" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <line className="orn--rule" x1="0" y1="15" x2="500" y2="15" strokeWidth="1" />
      <line className="orn--rule" x1="668" y1="15" x2="1168" y2="15" strokeWidth="1" />
      <path
        className="orn--brass"
        d="M500 15 C 520 15, 520 5, 540 5 C 560 5, 560 15, 584 15 C 608 15, 608 25, 628 25 C 648 25, 648 15, 668 15"
        fill="none"
        strokeWidth="1.1"
      />
      <path
        className="orn--brass"
        d="M500 15 C 520 15, 520 25, 540 25 C 560 25, 560 15, 584 15 C 608 15, 608 5, 628 5 C 648 5, 648 15, 668 15"
        fill="none"
        strokeWidth="1.1"
      />
    </svg>
  )
}

/**
 * The hero on ink. This is the reading path one scale up: stops joined in an
 * order, with a few stars on no path at all, the way some books sit outside
 * every series. It is the design's one idea drawn large, so do not replace it
 * with a gradient or a photograph.
 */
export function Constellation() {
  const stops: [number, number, 'brass' | 'paper'][] = [
    [40, 150, 'brass'],
    [120, 96, 'paper'],
    [205, 124, 'brass'],
    [290, 62, 'paper'],
    [385, 88, 'brass'],
    [470, 40, 'paper'],
    [560, 76, 'brass'],
  ]
  const loose: [number, number][] = [
    [80, 40],
    [250, 170],
    [430, 160],
    [600, 150],
  ]
  return (
    <svg className="constellation" viewBox="0 0 640 200" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <polyline
        className="orn--ink-faint"
        points={stops.map(([x, y]) => `${String(x)},${String(y)}`).join(' ')}
        fill="none"
        strokeWidth="0.7"
      />
      {stops.map(([x, y, tone]) => (
        <circle
          key={`${String(x)}-${String(y)}`}
          className={tone === 'brass' ? 'orn--fill-brass-lit' : 'orn--fill-on-ink'}
          cx={x}
          cy={y}
          r="2.6"
        />
      ))}
      {loose.map(([x, y]) => (
        <circle key={`${String(x)}-${String(y)}`} className="orn--fill-on-ink-soft" cx={x} cy={y} r="1.4" />
      ))}
    </svg>
  )
}
